<template>
    <div class="manage-guest-groups">

        <!-- Guest Phone Number Prompt -->
        <div v-if="msg.type === 'guest_group_flow' && isGuestPhonePrompt"
             class="gg-text-message gg-phone-card">

            <div class="gg-phone-question">
                <span v-html="formatPhonePrompt(responseText)"></span>
            </div>

            <div class="gg-phone-row">
                <Dropdown v-model="phoneForm.countryCode"
                          :options="countryCodeList"
                          optionValue="value"
                          optionLabel="text"
                          placeholder="Code"
                          filter
                          :disabled="disabled || phoneSubmitted"
                          class="gg-country-dropdown"
                          @change="onGuestCountryCodeChange" />

                <input v-model="phoneForm.phoneNumber"
                       type="text"
                       inputmode="numeric"
                       class="gg-phone-input"
                       :disabled="disabled || phoneSubmitted"
                       :placeholder="phonePlaceholder"
                       @input="onGuestPhoneInput" />
            </div>

            <div v-if="phoneError"
                 class="gg-phone-error gg-phone-error--standalone">
                {{ phoneError }}
            </div>

            <button type="button"
                    class="gg-btn gg-btn--primary gg-phone-submit"
                    :disabled="disabled || phoneSubmitted || !isPhoneComplete"
                    @click="submitGuestPhone">
                {{ phoneSubmitted ? 'Please wait...' : 'Continue' }}
            </button>
        </div>

        <!-- Guest Group Preview -->
        <div v-else-if="msg.type === 'guest_group_preview'"
             class="gg-summary-card">
            <div class="gg-summary-header">
                <div>
                    <div class="gg-header-title">Guest Group Summary</div>
                    <div class="gg-header-subtitle">
                        {{ responseText || 'Please review before saving' }}
                    </div>
                </div>
            </div>

            <div class="gg-summary-body gg-summary-edit-body">
                <div class="gg-form-field">
                    <label class="gg-form-label">
                        Group Name <span class="gg-required">*</span>
                    </label>

                    <input v-model="previewForm.groupName"
                           type="text"
                           class="gg-form-input"
                           maxlength="200"
                           placeholder="Enter Group Name"
                           :disabled="disabled" />
                </div>

                <div class="gg-preview-section-title">Guest Details</div>

                <div class="gg-form-field">
                    <label class="gg-form-label">
                        Full Name <span class="gg-required">*</span>
                    </label>

                    <input v-model="previewForm.fullName"
                           type="text"
                           class="gg-form-input"
                           maxlength="200"
                           placeholder="Enter Full Name"
                           :disabled="disabled" />
                </div>

                <div class="gg-form-field">
                    <label class="gg-form-label">
                        Phone Number <span class="gg-required">*</span>
                    </label>

                    <div class="gg-phone-row gg-phone-row--preview">
                        <Dropdown v-model="previewForm.countryCode"
                                  :options="countryCodeList"
                                  optionValue="value"
                                  optionLabel="text"
                                  placeholder="Code"
                                  filter
                                  :disabled="disabled"
                                  class="gg-country-dropdown"
                                  @change="onPreviewCountryCodeChange" />

                        <input v-model="previewForm.phoneNumber"
                               type="text"
                               inputmode="numeric"
                               class="gg-phone-input"
                               :placeholder="previewPhonePlaceholder"
                               :disabled="disabled"
                               @input="onPreviewPhoneInput" />
                    </div>

                    <div v-if="previewFormErrors.phoneNumber"
                         class="gg-field-error">
                        {{ previewFormErrors.phoneNumber }}
                    </div>
                </div>

                <div class="gg-form-field gg-form-field--last">
                    <label class="gg-form-label">Email</label>

                    <input v-model="previewForm.email"
                           type="email"
                           class="gg-form-input"
                           maxlength="250"
                           placeholder="Enter Email"
                           :disabled="disabled" />

                    <div v-if="previewFormErrors.email"
                         class="gg-field-error">
                        {{ previewFormErrors.email }}
                    </div>
                </div>
            </div>

            <div class="gg-actions gg-actions--stacked">
                <button type="button"
                        class="gg-btn gg-btn--primary"
                        :disabled="disabled || !canSavePreview"
                        @click="confirmGroup">
                    Confirm &amp; Save
                </button>

                <button type="button"
                        class="gg-btn gg-btn--cancel"
                        :disabled="disabled"
                        @click="$emit('cancel')">
                    Cancel
                </button>
            </div>
        </div>

        <!-- Guest Group Created / Failed -->
        <div v-else-if="msg.type === 'guest_group_created' || msg.type === 'guest_group_failed'" class="ticket-preview-card">
            <div class="ticket-preview-header" :class="{ 'gg-error-header': isError }">

                <div>
                    <h4>{{ isError ? 'Not Saved' : 'Guest Group Saved!' }}</h4>
                    <p>{{ isError ? 'Something went wrong.' : 'Your guest group has been saved.' }}</p>
                </div>
            </div>

            <div class="ticket-preview-body">
                <div class="ticket-preview-row">
                    <span class="tp-label">Group</span>
                    <span class="tp-value">{{ guestGroupData.groupName || '—' }}</span>
                </div>
                <div class="ticket-preview-row">
                    <span class="tp-label">Guests</span>
                    <span class="tp-value">{{ resultGuests.length }}</span>
                </div>
                <div v-if="isError" class="ticket-preview-error">
                    <span class="tp-err-icon">⚠️</span>
                    <p><strong>Error:</strong> {{ guestGroupData.error || 'Please try again.' }}</p>
                </div>
            </div>

            <div class="ticket-preview-footer">
                <span> {{ formatMMDDYYYY(currentDate) }}</span>
                <span :style="{ color: isError ? '#dc2626' : '#16a34a' }">{{ isError ? 'Not Saved' : 'Saved' }}</span>
            </div>
        </div>

        <!-- Guest Group List -->
        <div v-else-if="msg.type === 'view_guest_groups'" class="ticket-preview-card">
            <div class="ticket-preview-header">

                <div><h4>{{ guestGroupData.heading || 'Your Guest Groups' }}</h4></div>
                <span v-if="groups.length" class="ticket-preview-number">{{ groups.length }} group(s)</span>
            </div>

            <div class="ticket-preview-body gg-list-body">
                <div v-if="!groups.length" class="gg-empty">No guest groups found.</div>

                <div v-for="(group, index) in groups"
                     :key="group.guestGroupId || index"
                     class="gg-list-item"
                     :class="{ 'gg-list-item--last': index === groups.length - 1 }">
                    <div class="gg-list-main">
                        <div class="gg-list-title">{{ group.groupName }}</div>
                        <div class="gg-list-meta"> {{ group.guestCount || 0 }} guest(s) ·  {{ formatMMDDYYYY(group.createdDate) }}</div>
                    </div>
                    <div class="gg-list-actions">
                        <button type="button" class="gg-link gg-link--accent" :disabled="disabled" @click="viewDetails(group)">Details ▼</button>
                        <button type="button" class="gg-link gg-link--success" :disabled="disabled" @click="addGuest(group)">Add Guest</button>
                    </div>
                </div>
            </div>
        </div>

        <!-- Guest Group Detail -->
        <div v-else-if="msg.type === 'guest_group_detail'" class="ticket-preview-card">
            <div class="ticket-preview-header">

                <div><h4>{{ detail.groupName || 'Guest Group' }}</h4></div>
            </div>

            <div class="ticket-preview-body">
                <div v-if="!detailGuests.length" class="gg-empty">No guests yet.</div>

                <div v-for="(guest, index) in detailGuests"
                     :key="guest.guestGroupMapId || guest.guestId || index"
                     class="gg-guest-row">
                    <div>
                        <div class="gg-guest-name"> {{ guest.fullName || '—' }}</div>
                        <div class="gg-guest-contact">
                            {{ guest.email || '' }}<span v-if="guest.email && guest.phoneNumber"> · </span>{{ guest.phoneNumber || '' }}
                        </div>
                    </div>
                    <button type="button" class="gg-link gg-link--danger" :disabled="disabled" @click="askRemoveGuest(guest)">Remove</button>
                </div>

                <div class="gg-add-wrap">
                    <button type="button" class="gg-btn gg-btn--secondary" :disabled="disabled" @click="addGuest(detail)">Add Guest</button>
                </div>
            </div>
        </div>

        <!-- Text-only guest-group states -->
        <div v-else-if="textOnlyTypes.includes(msg.type)"
             class="gg-text-message"
             v-html="formatGuestGroupText(responseText)">
        </div>

        <!-- Remove Guest Confirmation -->
        <div v-if="removeGuestModal" class="gg-modal-overlay" @click.self="closeRemoveGuestModal">
            <div class="gg-modal">
                <div class="gg-modal-header">Remove Guest</div>
                <div class="gg-modal-body">
                    Are you sure you want to remove "<strong>{{ selectedGuest.fullName }}</strong>" from this guest group?
                </div>
                <div class="gg-modal-actions">
                    <button type="button" class="gg-modal-btn gg-modal-btn--cancel" @click="closeRemoveGuestModal">Cancel</button>
                    <button type="button" class="gg-modal-btn gg-modal-btn--ok" :disabled="disabled" @click="confirmRemoveGuest">Ok</button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
    import Dropdown from 'primevue/dropdown';
    import UserService from '@/service/user.service.js';
    import {
        findDefaultUsCountryCode,
        resolvePhoneLength,
    } from '@/helpers/phoneCountryHelpers.js';

    function formatMMDDYYYY(value) {
        if (!value) return '—';
        const text = String(value).trim();

        const iso = text.match(/^(\d{4})-(\d{2})-(\d{2})/);
        if (iso) return `${iso[2]}-${iso[3]}-${iso[1]}`;

        const date = text.match(/^(\d{1,2})[\/-](\d{1,2})[\/-](\d{4})$/);
        if (date) return `${date[1].padStart(2, '0')}-${date[2].padStart(2, '0')}-${date[3]}`;

        return text;
    }

    export default {
        name: 'ManageGuestGroups',
        components: {
            Dropdown,
        },
        props: {
            msg: { type: Object, required: true },
            disabled: { type: Boolean, default: false },
            currentDate: { type: String, default: '' },
            guestDraft: {
                type: Object,
                default: () => ({}),
            },
        },
        emits: [
            'confirm-group',
            'cancel',
            'group-detail',
            'add-guest',
            'remove-guest',
            'phone-submit',
            'phone-validity-change',
        ],
        data() {
            return {
                removeGuestModal: false,
                selectedGuest: {},

                countryCodeList: [],
                phoneForm: {
                    countryCode: '',
                    phoneNumber: '',
                },
                phoneLength: 10,
                phoneError: '',
                phoneSubmitted: false,

                guestForm: {
                    fullName: '',
                    email: '',
                },
                guestFormErrors: {
                    fullName: '',
                    email: '',
                },

                previewForm: {
                    groupName: '',
                    fullName: '',
                    email: '',
                    countryCode: '',
                    phoneNumber: '',
                },
                previewPhoneLength: 10,
                previewFormErrors: {
                    phoneNumber: '',
                    email: '',
                },

                textOnlyTypes: [
                    'guest_group_flow',
                    'guest_group_cancelled',
                    'view_guest_groups_empty',
                    'view_guest_groups_failed',
                    'guest_group_detail_failed',
                    'guest_group_guest_delete_failed',
                ],
            };
        },
        async created() {
            await this.GetCountryCodeList();
        },
        computed: {
            payload() {
                return this.msg && this.msg.payload ? this.msg.payload : {};
            },
            guestGroupData() {
                return this.payload.guest_group_data || {};
            },
            prefilled() {
                return this.guestGroupData.prefilled || {};
            },
            prefilledGuests() {
                return Array.isArray(this.prefilled.guests) ? this.prefilled.guests : [];
            },
            groups() {
                return Array.isArray(this.guestGroupData.groups) ? this.guestGroupData.groups : [];
            },
            detail() {
                return this.guestGroupData.detail || {};
            },
            detailGuests() {
                return Array.isArray(this.guestGroupData.guests) ? this.guestGroupData.guests : [];
            },
            resultGuests() {
                return Array.isArray(this.guestGroupData.guests) ? this.guestGroupData.guests : [];
            },
            responseText() {
                return this.payload.response || this.payload.formal_response || this.msg.text || '';
            },
            isGuestPhonePrompt() {
                if (this.msg.type !== 'guest_group_flow') return false;

                const text = String(this.responseText || '')
                    .replace(/<[^>]*>/g, ' ')
                    .replace(/\*\*/g, '')
                    .toLowerCase();

                return text.includes('phone number')
                    || text.includes('phone no')
                    || text.includes('phone');
            },
            phonePlaceholder() {
                return this.buildPhonePlaceholder(this.phoneLength || 10);
            },

            isPhoneComplete() {
                const digits = String(this.phoneForm.phoneNumber || '')
                    .replace(/\D/g, '');

                return digits.length === Number(this.phoneLength || 10);
            },

            canContinueGuest() {
                return (
                    !!String(this.guestForm.fullName || '').trim() &&
                    !!this.phoneForm.countryCode &&
                    this.isPhoneComplete &&
                    this.isValidOptionalEmail(this.guestForm.email)
                );
            },

            previewPhonePlaceholder() {
                return this.buildPhonePlaceholder(this.previewPhoneLength || 10);
            },

            isPreviewPhoneComplete() {
                const digits = String(this.previewForm.phoneNumber || '')
                    .replace(/\D/g, '');

                return digits.length === Number(this.previewPhoneLength || 10);
            },

            canSavePreview() {
                return (
                    !!String(this.previewForm.groupName || '').trim() &&
                    !!String(this.previewForm.fullName || '').trim() &&
                    !!this.previewForm.countryCode &&
                    this.isPreviewPhoneComplete &&
                    this.isValidOptionalEmail(this.previewForm.email)
                );
            },

            isError() {
                return this.msg.type === 'guest_group_failed';
            },
        },

        watch: {
            msg: {
                immediate: true,
                deep: true,
                handler() {
                    this.initializeGuestForms();
                },
            },

            guestDraft: {
                immediate: true,
                deep: true,
                handler() {
                    this.initializeGuestForms();
                },
            },
        },

        methods: {
            formatMMDDYYYY,

            formatGuestGroupText(text) {
                if (!text) return '';

                return String(text)
                    .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
            },
            formatPhonePrompt(text) {
                return String(text || '')
                    .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
            },

            buildPhonePlaceholder(length) {
                const requiredLength = Number(length || 10);

                if (requiredLength === 10) return '(000)-000-0000';
                if (requiredLength === 7) return '000-0000';
                if (requiredLength === 8) return '0000-0000';
                if (requiredLength === 9) return '000-000-000';
                if (requiredLength === 11) return '000-0000-0000';

                return requiredLength > 0
                    ? '0'.repeat(requiredLength)
                    : 'Phone number';
            },

            formatPhoneByLength(digits, length) {
                const clean = String(digits || '').replace(/\D/g, '');
                const requiredLength = Number(length || 10);

                if (requiredLength === 10) {
                    if (clean.length > 6) {
                        return `(${clean.slice(0, 3)}) ${clean.slice(3, 6)}-${clean.slice(6)}`;
                    }
                    if (clean.length > 3) {
                        return `(${clean.slice(0, 3)}) ${clean.slice(3)}`;
                    }
                    if (clean.length > 0) {
                        return `(${clean}`;
                    }
                    return '';
                }

                if (requiredLength === 7) {
                    return clean.length > 3
                        ? `${clean.slice(0, 3)}-${clean.slice(3)}`
                        : clean;
                }

                if (requiredLength === 8) {
                    return clean.length > 4
                        ? `${clean.slice(0, 4)}-${clean.slice(4)}`
                        : clean;
                }

                if (requiredLength === 9) {
                    if (clean.length > 6) {
                        return `${clean.slice(0, 3)}-${clean.slice(3, 6)}-${clean.slice(6)}`;
                    }
                    if (clean.length > 3) {
                        return `${clean.slice(0, 3)}-${clean.slice(3)}`;
                    }
                    return clean;
                }

                if (requiredLength === 11) {
                    if (clean.length > 7) {
                        return `${clean.slice(0, 3)}-${clean.slice(3, 7)}-${clean.slice(7)}`;
                    }
                    if (clean.length > 3) {
                        return `${clean.slice(0, 3)}-${clean.slice(3)}`;
                    }
                    return clean;
                }

                return clean;
            },

            isValidOptionalEmail(value) {
                const email = String(value || '').trim();
                if (!email) return true;

                return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
            },

            clearGuestFormError(field) {
                if (field && this.guestFormErrors[field] !== undefined) {
                    this.guestFormErrors[field] = '';
                }
            },

            initializeGuestForms() {
                const pre = this.prefilled || {};
                const guests = Array.isArray(pre.guests) ? pre.guests : [];
                const firstGuest = guests[0] || {};
                const draft = this.guestDraft || {};

                const fullName =
                    draft.fullName ||
                    firstGuest.fullName ||
                    pre.fullName ||
                    '';

                const email =
                    draft.email ||
                    firstGuest.email ||
                    pre.email ||
                    '';

                if (!this.guestForm.fullName) {
                    this.guestForm.fullName = fullName;
                }

                if (!this.guestForm.email) {
                    this.guestForm.email = email;
                }

                if (
                    this.msg?.type === 'guest_group_preview' &&
                    !this.previewForm.groupName
                ) {
                    const draftCountryCodeText =
                        draft.countryCodeText ||
                        firstGuest.countryCodeText ||
                        pre.countryCodeText ||
                        '';

                    let draftCountryCode =
                        draft.countryCode ||
                        firstGuest.countryCode ||
                        pre.countryCode ||
                        '';

                    // If only the displayed country text/dial code was preserved
                    // (for example "+501 (Belize)"), resolve it back to the actual
                    // country-code dropdown value once the list is available.
                    if (!draftCountryCode && draftCountryCodeText && this.countryCodeList.length) {
                        const normalizedText = String(draftCountryCodeText).trim().toLowerCase();
                        const dialCode = String(draftCountryCodeText).trim().split(/\s+/)[0];

                        const matchedCountry = this.countryCodeList.find(item => {
                            const itemText = String(item?.text || '').trim();
                            const itemTextLower = itemText.toLowerCase();
                            const itemDial = itemText.split(/\s+/)[0];

                            return (
                                itemTextLower === normalizedText ||
                                (!!dialCode && itemDial === dialCode)
                            );
                        });

                        if (matchedCountry) {
                            draftCountryCode = matchedCountry.value;
                        }
                    }

                    if (!draftCountryCode) {
                        draftCountryCode = this.phoneForm.countryCode || '';
                    }

                    this.previewForm = {
                        groupName: pre.groupName || this.guestGroupData.groupName || '',
                        fullName: fullName,
                        email: email,
                        countryCode: draftCountryCode,
                        phoneNumber:
                            draft.phoneNumber ||
                            firstGuest.phoneNumber ||
                            pre.phoneNumber ||
                            '',
                    };

                    this.previewPhoneLength =
                        resolvePhoneLength(
                            this.previewForm.countryCode,
                            this.countryCodeList
                        ) || 10;

                    const previewDigits = String(this.previewForm.phoneNumber || '')
                        .replace(/\D/g, '')
                        .slice(-this.previewPhoneLength);

                    this.previewForm.phoneNumber =
                        this.formatPhoneByLength(
                            previewDigits,
                            this.previewPhoneLength
                        );
                }
            },

            async GetCountryCodeList() {
                try {
                    const response = await UserService.countryList(
                        'countryCode',
                        '00Test',
                        localStorage.getItem('companyId')
                    );

                    this.countryCodeList = response.data || [];

                    const us = findDefaultUsCountryCode(this.countryCodeList)
                        || this.countryCodeList.find((item) =>
                            String(item.text || '').includes('US')
                        );

                    if (us) {
                        if (!this.phoneForm.countryCode) {
                            this.phoneForm.countryCode = us.value;
                        }

                        this.phoneLength =
                            resolvePhoneLength(
                                this.phoneForm.countryCode,
                                this.countryCodeList
                            ) || 10;
                    }

                    this.initializeGuestForms();

                    // Preview may have been initialized before country codes
                    // finished loading. Re-resolve the selected country's
                    // actual phone length now so Confirm & Save is not
                    // incorrectly disabled.
                    if (this.msg?.type === 'guest_group_preview') {
                        // Re-resolve the selected country after the country-code
                        // list is loaded. This fixes the preview dropdown showing
                        // "Code" even though the previous phone step had a country.
                        if (!this.previewForm.countryCode) {
                            const draft = this.guestDraft || {};

                            const preferredText =
                                draft.countryCodeText ||
                                '';

                            const fullPhone =
                                draft.fullPhoneNumber ||
                                '';

                            const preferredDial =
                                preferredText
                                    ? String(preferredText).trim().split(/\s+/)[0]
                                    : (
                                        String(fullPhone).trim().match(/^\+\d+(?:-\d+)?/)?.[0] ||
                                        ''
                                    );

                            const matchedCountry =
                                this.countryCodeList.find(item => {
                                    const itemText = String(item?.text || '').trim();
                                    const itemDial = itemText.split(/\s+/)[0];

                                    return (
                                        (preferredText &&
                                            itemText.toLowerCase() ===
                                            String(preferredText).trim().toLowerCase()) ||
                                        (preferredDial && itemDial === preferredDial)
                                    );
                                });

                            if (matchedCountry) {
                                this.previewForm.countryCode = matchedCountry.value;
                            }
                        }

                        if (this.previewForm.countryCode) {
                            this.previewPhoneLength =
                                resolvePhoneLength(
                                    this.previewForm.countryCode,
                                    this.countryCodeList
                                ) || 10;

                            const previewDigits =
                                String(this.previewForm.phoneNumber || '')
                                    .replace(/\D/g, '')
                                    .slice(-this.previewPhoneLength);

                            this.previewForm.phoneNumber =
                                this.formatPhoneByLength(
                                    previewDigits,
                                    this.previewPhoneLength
                                );
                        }
                    }
                } catch (e) {
                    console.error('Country code load failed', e);
                }
            },

            onGuestCountryCodeChange() {
                this.phoneLength =
                    resolvePhoneLength(
                        this.phoneForm.countryCode,
                        this.countryCodeList
                    ) || 10;

                this.phoneForm.phoneNumber = '';
                this.phoneError = '';
                this.phoneSubmitted = false;

                this.$emit('phone-validity-change', false);
            },

            onGuestPhoneInput(event) {
                const maxLength = Number(this.phoneLength || 10);

                const digits = String(event?.target?.value || '')
                    .replace(/\D/g, '')
                    .slice(0, maxLength);

                this.phoneForm.phoneNumber =
                    this.formatPhoneByLength(digits, maxLength);

                this.phoneError = '';
                this.phoneSubmitted = false;

                this.$emit(
                    'phone-validity-change',
                    digits.length === maxLength
                );
            },

            onPreviewCountryCodeChange() {
                this.previewPhoneLength =
                    resolvePhoneLength(
                        this.previewForm.countryCode,
                        this.countryCodeList
                    ) || 10;

                this.previewForm.phoneNumber = '';
                this.previewFormErrors.phoneNumber = '';
            },

            onPreviewPhoneInput(event) {
                const maxLength = Number(this.previewPhoneLength || 10);

                const digits = String(event?.target?.value || '')
                    .replace(/\D/g, '')
                    .slice(0, maxLength);

                this.previewForm.phoneNumber =
                    this.formatPhoneByLength(digits, maxLength);

                this.previewFormErrors.phoneNumber = '';
            },

            resolveGuestDialCode() {
                const value = this.phoneForm.countryCode;
                if (!value) return '';

                const selected = (this.countryCodeList || [])
                    .find((item) => item.value === value);

                if (!selected || !selected.text) return value;

                const dialCode = String(selected.text).trim().split(/\s+/)[0];
                return dialCode || value;
            },

            submitGuestPhone() {
                this.phoneError = '';
                this.guestFormErrors = {
                    fullName: '',
                    email: '',
                };

                const fullName = String(this.guestForm.fullName || '').trim();
                const email = String(this.guestForm.email || '').trim();

                if (!this.phoneForm.countryCode) {
                    this.phoneError = 'Please select a country code.';
                    return;
                }

                const requiredLength = Number(this.phoneLength || 10);

                const digits = String(this.phoneForm.phoneNumber || '')
                    .replace(/\D/g, '');

                if (digits.length !== requiredLength) {
                    this.phoneError =
                        `Enter a valid ${requiredLength}-digit phone number.`;
                    return;
                }

                const dialCode = this.resolveGuestDialCode();
                const formattedPhone =
                    this.formatPhoneByLength(digits, requiredLength);

                const fullPhoneNumber = dialCode
                    ? `${dialCode} ${formattedPhone}`
                    : formattedPhone;

                const selectedCountry =
                    (this.countryCodeList || []).find(
                        item =>
                            String(item.value) ===
                            String(this.phoneForm.countryCode)
                    );

                this.phoneSubmitted = true;
                this.$emit('phone-validity-change', false);

                this.$emit('phone-submit', {
                    fullName,
                    email,
                    countryCode: this.phoneForm.countryCode,
                    countryCodeText: selectedCountry?.text || '',
                    phoneNumber: formattedPhone,
                    fullPhoneNumber,
                });
            },

            confirmGroup() {
                this.previewFormErrors = {
                    phoneNumber: '',
                    email: '',
                };

                const groupName =
                    String(this.previewForm.groupName || '').trim();

                const fullName =
                    String(this.previewForm.fullName || '').trim();

                const email =
                    String(this.previewForm.email || '').trim();

                if (!groupName || !fullName) {
                    return;
                }

                if (!this.previewForm.countryCode) {
                    const draft = this.guestDraft || {};
                    const preferredText = draft.countryCodeText || '';
                    const preferredDial =
                        preferredText
                            ? String(preferredText).trim().split(/\s+/)[0]
                            : (
                                String(draft.fullPhoneNumber || '')
                                    .trim()
                                    .match(/^\+\d+(?:-\d+)?/)?.[0] ||
                                ''
                            );

                    const matchedCountry =
                        this.countryCodeList.find(item => {
                            const itemText = String(item?.text || '').trim();
                            const itemDial = itemText.split(/\s+/)[0];

                            return (
                                (preferredText &&
                                    itemText.toLowerCase() ===
                                    String(preferredText).trim().toLowerCase()) ||
                                (preferredDial && itemDial === preferredDial)
                            );
                        });

                    if (matchedCountry) {
                        this.previewForm.countryCode = matchedCountry.value;
                        this.previewPhoneLength =
                            resolvePhoneLength(
                                matchedCountry.value,
                                this.countryCodeList
                            ) || 10;
                    }
                }

                if (!this.previewForm.countryCode) {
                    this.previewFormErrors.phoneNumber =
                        'Please select a country code.';
                    return;
                }

                if (!this.isValidOptionalEmail(email)) {
                    this.previewFormErrors.email =
                        'Enter a valid email address.';
                    return;
                }

                const requiredLength =
                    Number(this.previewPhoneLength || 10);

                const digits =
                    String(this.previewForm.phoneNumber || '')
                        .replace(/\D/g, '');

                if (digits.length !== requiredLength) {
                    this.previewFormErrors.phoneNumber =
                        `Enter a valid ${requiredLength}-digit phone number.`;
                    return;
                }

                const selectedCountry =
                    (this.countryCodeList || []).find(
                        item =>
                            String(item.value) ===
                            String(this.previewForm.countryCode)
                    );

                const dialCode = selectedCountry?.text
                    ? String(selectedCountry.text).trim().split(/\s+/)[0]
                    : '';

                const formattedPhone =
                    this.formatPhoneByLength(
                        digits,
                        requiredLength
                    );

                const fullPhoneNumber = dialCode
                    ? `${dialCode} ${formattedPhone}`
                    : formattedPhone;

                const existingGuests =
                    Array.isArray(this.prefilled.guests)
                        ? [...this.prefilled.guests]
                        : [];

                const guest = {
                    ...(existingGuests[0] || {}),
                    fullName,
                    email,
                    phoneNumber: fullPhoneNumber,
                    countryCode: this.previewForm.countryCode,
                    countryCodeText: selectedCountry?.text || '',
                };

                if (existingGuests.length) {
                    existingGuests[0] = guest;
                } else {
                    existingGuests.push(guest);
                }

                this.$emit('confirm-group', {
                    ...this.prefilled,
                    groupName,
                    guests: existingGuests,
                });
            },
            viewDetails(group) {
                this.$emit('group-detail', group);
            },
            addGuest(group) {
                this.$emit('add-guest', {
                    guestGroupId: group.guestGroupId,
                    groupName: group.groupName,
                });
            },
            askRemoveGuest(guest) {
                if (!guest || !guest.guestGroupMapId) return;
                this.selectedGuest = { ...guest };
                this.removeGuestModal = true;
            },
            closeRemoveGuestModal() {
                this.removeGuestModal = false;
                this.selectedGuest = {};
            },
            confirmRemoveGuest() {
                const guest = { ...this.selectedGuest };
                this.closeRemoveGuestModal();
                this.$emit('remove-guest', {
                    guestGroupMapId: guest.guestGroupMapId || '',
                    guestId: guest.guestId || '',
                    guestGroupId: this.detail.guestGroupId || '',
                    guestName: guest.fullName || '',
                    groupName: this.detail.groupName || '',
                });
            },
        },
    };
</script>

<style scoped>
    .manage-guest-groups {
        width: 100%;
        font-family: Outfit, sans-serif;
    }

    .gg-summary-card,
    .ticket-preview-card {
        background: #fff;
        border-radius: 16px;
        box-shadow: 0 4px 18px rgba(0, 0, 0, 0.12);
        overflow: hidden;
        font-size: 13px;
        min-width: 300px;
        max-width: 360px;
    }

    .gg-summary-header,
    .ticket-preview-header {
        background: linear-gradient(135deg, #0f66b8 0%, #122341 100%);
        color: #fff;
        padding: 14px 18px;
        display: flex;
        align-items: center;
        gap: 10px;
    }

    .gg-error-header {
        background: linear-gradient(135deg, #991b1b, #dc2626) !important;
    }

    .gg-header-icon,
    .tp-icon {
        font-size: 22px;
    }

    .gg-header-title,
    .ticket-preview-header h4 {
        margin: 0;
        font-weight: 700;
        font-size: 14px;
    }

    .gg-header-subtitle,
    .ticket-preview-header p {
        margin: 2px 0 0;
        font-size: 11px;
        opacity: 0.8;
    }

    .gg-summary-body,
    .ticket-preview-body {
        padding: 14px 18px;
    }

    .gg-row,
    .ticket-preview-row {
        display: flex;
        justify-content: space-between;
        gap: 10px;
        border-bottom: 1px solid #f0f0f0;
        padding-bottom: 8px;
        margin-bottom: 8px;
    }

    .gg-label,
    .tp-label {
        color: #888;
        font-size: 11px;
        font-weight: 600;
    }

    .gg-value,
    .tp-value {
        color: #1e2d45;
        font-size: 12px;
        font-weight: 600;
        text-align: right;
    }

    .gg-preview-guest {
        font-size: 12px;
        padding: 5px 0;
        border-bottom: 1px solid #f5f5f5;
    }

    .gg-empty-inline,
    .gg-empty {
        color: #999;
        font-size: 12px;
        text-align: center;
        padding: 10px 0;
    }

    .gg-actions {
        padding: 0 18px 16px;
        display: flex;
        gap: 8px;
    }

    .gg-actions--stacked {
        flex-direction: column;
    }

    .gg-btn {
        border-radius: 10px;
        padding: 10px;
        font-size: 13px;
        font-weight: 600;
        cursor: pointer;
    }

        .gg-btn:disabled,
        .gg-link:disabled,
        .gg-modal-btn:disabled {
            opacity: 0.45;
            cursor: not-allowed;
            pointer-events: none;
        }

    .gg-btn--primary {
        background: linear-gradient(135deg, #0f66b8 0%, #122341 100%);
        color: #fff;
        border: none;
        font-weight: 700;
    }

    .gg-btn--secondary {
        width: 100%;
        background: #f1f5f9;
        color: #1e2d45;
        border: 1.5px solid #cbd5e1;
    }

    .gg-btn--cancel {
        background: none;
        border: none;
        color: #aaa;
        font-size: 12px;
        text-decoration: underline;
    }

    .ticket-preview-number {
        margin-left: auto;
        font-size: 11px;
        font-weight: 600;
    }

    .gg-list-body {
        padding: 0 16px 8px;
    }

    .gg-list-item {
        padding: 10px 0;
        border-bottom: 1px solid #e5e7eb;
        display: flex;
        justify-content: space-between;
        align-items: flex-start;
        gap: 8px;
    }

    .gg-list-item--last {
        border-bottom: none;
    }

    .gg-list-main {
        flex: 1;
    }

    .gg-list-title {
        color: #1e2d45;
        font-size: 13px;
        font-weight: 600;
    }

    .gg-list-meta {
        color: #64748b;
        font-size: 11px;
        margin-top: 3px;
    }

    .gg-list-actions {
        display: flex;
        flex-direction: column;
        align-items: flex-end;
        gap: 4px;
    }

    .gg-link {
        background: none;
        border: none;
        padding: 0;
        font-size: 11px;
        font-weight: 600;
        cursor: pointer;
    }

    .gg-link--accent {
        color: #0f66b8;
    }

    .gg-link--success {
        color: #16a34a;
    }

    .gg-link--danger {
        color: #dc2626;
    }

    .gg-guest-row {
        display: flex;
        justify-content: space-between;
        align-items: center;
        gap: 8px;
        padding: 7px 0;
        border-bottom: 1px dashed #f0f0f0;
    }

    .gg-guest-name {
        font-size: 12px;
        font-weight: 600;
        color: #1e2d45;
    }

    .gg-guest-contact {
        margin-top: 2px;
        color: #64748b;
        font-size: 10px;
    }

    .gg-add-wrap {
        padding-top: 10px;
    }

    .ticket-preview-error {
        display: flex;
        gap: 8px;
        margin-top: 8px;
        padding: 10px;
        background: #fef2f2;
        border: 1px solid #fecaca;
        border-radius: 8px;
        color: #dc2626;
        font-size: 12px;
    }

        .ticket-preview-error p {
            margin: 0;
        }

    .ticket-preview-footer {
        display: flex;
        justify-content: space-between;
        align-items: center;
        border-top: 1px solid #f0f0f0;
        padding: 10px 18px;
        color: #64748b;
        font-size: 11px;
    }

    .gg-text-message {
        width: fit-content;
        min-width: 0;
        max-width: 330px;
        background: #ffffff;
        color: #333333;
        padding: 12px 16px;
        border-radius: 6px 16px 16px 16px;
        font-family: Outfit, sans-serif;
        font-size: 13px;
        font-weight: 400;
        line-height: 1.5;
        box-sizing: border-box;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
        word-break: normal;
        overflow-wrap: break-word;
    }

    .gg-phone-card {
        width: 360px;
        max-width: 360px;
        padding: 0;
        border-radius: 16px;
        overflow: visible;
    }

    .gg-guest-form {
        padding: 14px 16px 4px;
        background: #ffffff;
    }

    .gg-form-field {
        margin-bottom: 12px;
    }

    .gg-form-field--last {
        margin-bottom: 6px;
    }

    .gg-form-label {
        display: block;
        margin: 0 0 6px;
        color: #1e2d45;
        font-size: 11px;
        font-weight: 600;
    }

    .gg-required {
        color: #dc2626;
    }

    .gg-form-input {
        width: 100%;
        height: 40px;
        padding: 8px 10px;
        border: 1px solid #d7dce2;
        border-radius: 8px;
        background: #ffffff;
        color: #1e2d45;
        font-family: Outfit, sans-serif;
        font-size: 13px;
        outline: none;
        box-sizing: border-box;
    }

        .gg-form-input:focus {
            border-color: #0f66b8;
            box-shadow: 0 0 0 2px rgba(15, 102, 184, 0.12);
        }

    .gg-field-error {
        margin-top: 5px;
        color: #dc2626;
        font-size: 10px;
    }

    .gg-summary-edit-body {
        padding-bottom: 6px;
    }

    .gg-preview-section-title {
        margin: 3px 0 10px;
        padding-top: 8px;
        border-top: 1px solid #edf0f4;
        color: #1e2d45;
        font-size: 11px;
        font-weight: 700;
    }

    /* Phone question blue header */
    .gg-phone-question {
        width: 100%;
        padding: 13px 16px;
        margin: 0;
        box-sizing: border-box;
        background: linear-gradient( 135deg, #0f66b8 0%, #122341 100% );
        color: #ffffff;
        border-radius: 16px 16px 0 0;
        font-size: 13px;
        font-weight: 600;
        line-height: 1.4;
    }

    /* Country + phone */
    .gg-phone-row {
        display: flex;
        align-items: center;
        gap: 8px;
        width: 100%;
        padding: 14px 16px 10px;
        box-sizing: border-box;
        background: #ffffff;
    }

    .gg-summary-edit-body .gg-phone-row {
        padding: 0;
    }

    .gg-phone-row--preview {
        margin-top: 0;
    }

    .gg-country-dropdown {
        width: 125px;
        min-width: 125px;
        max-width: 125px;
        flex: 0 0 125px;
    }

    .gg-phone-input {
        flex: 1;
        width: 100%;
        min-width: 0;
        height: 40px;
        padding: 8px 10px;
        border: 1px solid #d7dce2;
        border-radius: 8px;
        background: #ffffff;
        color: #1e2d45;
        font-family: Outfit, sans-serif;
        font-size: 13px;
        outline: none;
        box-sizing: border-box;
    }

        .gg-phone-input:focus {
            border-color: #0f66b8;
            box-shadow: 0 0 0 2px rgba(15, 102, 184, 0.12);
        }

    /* Error */
    .gg-phone-error {
        padding: 0;
        margin: 5px 0 0;
        background: #ffffff;
        color: #dc2626;
        font-size: 11px;
    }

    /* Continue */
    .gg-phone-error--standalone {
        padding: 0 16px;
        margin: -2px 0 6px;
    }

    .gg-phone-submit {
        width: calc(100% - 32px);
        min-height: 40px;
        margin: 2px 16px 16px;
        border: 0;
        border-radius: 9px;
        background: linear-gradient( 135deg, #0f66b8 0%, #122341 100% );
        color: #ffffff;
        font-size: 13px;
        font-weight: 700;
        cursor: pointer;
    }

        .gg-phone-submit:disabled {
            opacity: 0.45;
            cursor: not-allowed;
        }

    /* PrimeVue Country Dropdown */
    :deep(.gg-country-dropdown.p-dropdown) {
        width: 125px;
        min-width: 125px;
        max-width: 125px;
        height: 40px;
        border: 1px solid #d7dce2;
        border-radius: 8px;
        background: #ffffff;
        box-shadow: none;
    }

    :deep(.gg-country-dropdown .p-dropdown-label) {
        padding: 9px 7px 9px 10px;
        font-size: 13px;
        color: #1e2d45;
        overflow: hidden;
        white-space: nowrap;
        text-overflow: ellipsis;
    }

    :deep(.gg-country-dropdown .p-dropdown-trigger) {
        width: 28px;
        min-width: 28px;
    }

    /* Country list popup */
    :deep(.p-dropdown-panel) {
        min-width: 180px !important;
        max-width: 240px !important;
        z-index: 99999 !important;
    }

    :deep(.p-dropdown-items-wrapper) {
        max-height: 210px !important;
    }

    :deep(.p-dropdown-item) {
        padding: 8px 10px;
        font-size: 12px;
        white-space: nowrap;
    }

    /* Mobile */
    @media (max-width: 480px) {
        .gg-text-message {
            max-width: 300px;
        }

        .gg-phone-card {
            width: 100%;
            max-width: 330px;
        }

        .gg-country-dropdown {
            width: 115px;
            min-width: 115px;
            max-width: 115px;
            flex-basis: 115px;
        }

        :deep(.gg-country-dropdown.p-dropdown) {
            width: 115px;
            min-width: 115px;
            max-width: 115px;
        }
    }

    .gg-modal-overlay {
        position: fixed;
        inset: 0;
        z-index: 9999;
        display: flex;
        align-items: center;
        justify-content: center;
        background: rgba(0, 0, 0, 0.5);
    }

    .gg-modal {
        width: 360px;
        max-width: calc(100vw - 30px);
        overflow: hidden;
        background: #fff;
        border-radius: 8px;
        box-shadow: 0 8px 30px rgba(0, 0, 0, 0.3);
    }

    .gg-modal-header {
        background: #1e2d45;
        color: #fff;
        padding: 14px 18px;
        font-size: 15px;
        font-weight: 700;
    }

    .gg-modal-body {
        padding: 20px 18px;
        color: #1e2d45;
        font-size: 13px;
    }

    .gg-modal-actions {
        display: flex;
        justify-content: flex-end;
        gap: 10px;
        padding: 14px 18px;
    }

    .gg-modal-btn {
        border: none;
        border-radius: 6px;
        padding: 8px 16px;
        color: #fff;
        font-weight: 600;
        cursor: pointer;
    }

    .gg-modal-btn--cancel {
        background: #dc2626;
    }

    .gg-modal-btn--ok {
        background: #1e2d45;
    }
</style>
